import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Image from '@components/atoms/Image';
import Button from '@components/atoms/Button';
import ProductSlider from '@components/molecules/ProductSlider';
import { RootState } from '@redux/store';
import { RelatedProduct } from '@interfaces/product';
import { RelatedProductPDP } from '@interfaces/relatedProducts';
import { trackAddToCart } from '@helpers/trackCartActions';
import { handleAddToCart } from '@helpers/global';
import { doConfetti } from '../../utils/confetti'

export interface RelatedProductsProps {
  productId: number;
  placeholderSvg: string;
}

const RelatedProducts: React.FC<RelatedProductsProps> = ({ productId, placeholderSvg }) => {
  const [recommendedProducts, setRecommendedProducts] = useState<RelatedProduct[]>([]);
  const currentVariant = useSelector((state: RootState) => state.PDP.PDPVariant);

  const fetchRecommendations = useCallback(async () => {
    try {
      const response = await fetch(
        `${window.Shopify?.routes?.root}recommendations/products.json?product_id=${productId}&limit=10&intent=related`
      );
      const data = await response.json();
      if (data.products?.length > 0) {
        const transformedProducts = data.products
          .filter((product: RelatedProductPDP) => product.vendor !== "SavedBy")
          .map((product: RelatedProductPDP) => ({
            id: product.id,
            variantId: product.variants[0].id,
            available: product.available || false,
            featuredImage: product?.featured_image || placeholderSvg,
            image: product?.variants[0]?.featured_image?.src,
            title: product.title,
            type: product.type,
            vendor: product.vendor,
            sku: product.variants[0]?.sku ? product.variants[0].sku : product.variants[0].id,
            price: new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: window.DEPT.localization.currencyCode || 'USD',
            }).format(product.price / 100),
            handle: product.handle,
            hasOnlyDefaultVariant: product.variants.length === 1 && product.variants[0].available,
          }));
        setRecommendedProducts(transformedProducts);
      }
    } catch (error) {
      console.error('Error fetching recommended products:', error);
    }
  }, [productId, placeholderSvg]);
  // vendor === "SavedBy"

  useEffect(() => {
    const relatedFromMetafields = currentVariant?.metafields?.relatedProducts ?? [];
    if (relatedFromMetafields.length > 0) {
      setRecommendedProducts(relatedFromMetafields);
    } else fetchRecommendations();
  }, [currentVariant, fetchRecommendations]);

  const handleAddToCartWithTracking = (product: RelatedProduct) => {
    handleAddToCart(product.variantId);
    trackAddToCart(
      {
        title: product.title,
        type: product.type,
        vendor: product.vendor,
      },
      {
        sku: product.sku,
        price: product.price.replace(/[^0-9.-]+/g, ''),
      },
    );
  };

  const slides = recommendedProducts.map((product) => (
    <div className="pb-6 h-full" key={product.id}>
      <div className="flex flex-col bg-white rounded-large shadow-md">
        <a href={`/products/${product.handle}`} aria-label={`Product Image Link for the ${product.title}`}>
          <div className="flex justify-center">
            <Image
              src={product.image || product.featuredImage}
              alt={product.title}
              classes="w-full h-full object-contain rounded-large aspect-square"
              srcWidths={{ sm: 304, md: 304, lg: 304, xl: 304, '2xl': 304 }}
              blendMode="multiply"
            />
          </div>
        </a>
      </div>
      <a href={`/products/${product.handle}`} aria-label={`Link to the ${product.title}`}>
        <div className="flex flex-wrap justify-between pt-6">
          <h3 className="body-md w-8/12 font-semibold truncate">{product.title}</h3>
          <p className="body-md w-4/12 text-right font-semibold">{product.price}</p>
        </div>
      </a>

      <p className="body-xs min-h-5.5">{product.type}</p>
      {product.hasOnlyDefaultVariant ? (
        <>
          {/* <Button
            mode="button"
            buttonText="Add to cart"
            onClick={() => handleAddToCartWithTracking(product)}
            className="mt-6 w-full min-w-max"
          /> */}
          <button
            type="button"
            className="button button--primary mt-6 w-full min-w-max"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.preventDefault()
              doConfetti(e.currentTarget)
              handleAddToCartWithTracking(product)
            }}
          >
            <div className="rectangle-1"></div>
            <div className="button-text">Add to cart</div>
          </button>
        </>
      ) : (
        <Button
          mode="link"
          url={`/products/${product.handle}`}
          buttonText="Learn More"
          className="mt-6 w-full min-w-max"
        />
      )}
    </div>
  ));

  return (
    <ProductSlider
      slides={slides}
      paginationClass="related-products-pagination"
      navigationPrevClass="related-products-button-prev"
      navigationNextClass="related-products-button-next"
    />
  );
};

export default RelatedProducts;
